import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Shield, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  useExpensePolicies,
  useCreateExpensePolicy,
  useUpdateExpensePolicy,
  useDeleteExpensePolicy,
  useExpenseCategories,
} from '@/hooks/use-payroll';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { ExpensePolicy, CreateExpensePolicyPayload } from '@/lib/types';

const emptyForm: CreateExpensePolicyPayload = {
  name: '',
  max_per_diem: null,
  max_single_expense: null,
  require_receipt_above: '25.00',
  require_manager_approval_above: '100.00',
  allowed_categories: [],
  is_active: true,
};

export function ExpensePoliciesPage() {
  const orgCurrency = useOrgCurrency();
  const { data: policies, isLoading } = useExpensePolicies();
  const { data: categories } = useExpenseCategories();
  const createPolicy = useCreateExpensePolicy();
  const updatePolicy = useUpdateExpensePolicy();
  const deletePolicy = useDeleteExpensePolicy();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CreateExpensePolicyPayload>(emptyForm);

  const handleEdit = (policy: ExpensePolicy) => {
    setEditingId(policy.id);
    setForm({
      name: policy.name,
      max_per_diem: policy.max_per_diem,
      max_single_expense: policy.max_single_expense,
      require_receipt_above: policy.require_receipt_above,
      require_manager_approval_above: policy.require_manager_approval_above,
      allowed_categories: policy.allowed_categories,
      is_active: policy.is_active,
    });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await updatePolicy.mutateAsync({ id: editingId, ...form });
    } else {
      await createPolicy.mutateAsync(form);
    }
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Delete this policy?')) {
      deletePolicy.mutate(id);
    }
  };

  const handleCategoryToggle = (catId: number) => {
    setForm((prev) => {
      const current = prev.allowed_categories ?? [];
      return {
        ...prev,
        allowed_categories: current.includes(catId)
          ? current.filter((id) => id !== catId)
          : [...current, catId],
      };
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/payroll">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Expense Policies</h1>
            <p className="text-gray-600 mt-1">Manage expense approval rules and limits</p>
          </div>
        </div>
        <Button onClick={handleNew}>
          <Plus className="h-4 w-4 mr-2" />
          New Policy
        </Button>
      </div>

      {/* Policy Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Policy' : 'New Policy'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Policy Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., Standard Policy"
                    required
                  />
                </div>
                <div>
                  <Label>Max Per Diem ({orgCurrency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.max_per_diem ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        max_per_diem: e.target.value || null,
                      }))
                    }
                    placeholder="No limit"
                  />
                </div>
                <div>
                  <Label>Max Single Expense ({orgCurrency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.max_single_expense ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        max_single_expense: e.target.value || null,
                      }))
                    }
                    placeholder="No limit"
                  />
                </div>
                <div>
                  <Label>Require Receipt Above ({orgCurrency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.require_receipt_above ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, require_receipt_above: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label>Require Manager Approval Above ({orgCurrency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.require_manager_approval_above ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        require_manager_approval_above: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {categories?.results && categories.results.length > 0 && (
                <div>
                  <Label>Allowed Categories (leave empty for all)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {categories.results.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleCategoryToggle(cat.id)}
                        className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                          (form.allowed_categories ?? []).includes(cat.id)
                            ? 'bg-blue-100 border-blue-300 text-blue-800'
                            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active ?? true}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm">Active</span>
              </label>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={createPolicy.isPending || updatePolicy.isPending}
                >
                  {editingId ? 'Update Policy' : 'Create Policy'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Policy List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading policies...</div>
          ) : !policies?.results?.length ? (
            <div className="p-8 text-center text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No expense policies configured</p>
              <p className="text-sm mt-1">Create a policy to enforce expense limits and approval rules</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {policies.results.map((policy) => (
                <div key={policy.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{policy.name}</h3>
                        <Badge variant={policy.is_active ? 'default' : 'secondary'}>
                          {policy.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                        {policy.max_single_expense && (
                          <span>Max expense: {formatMoney(policy.max_single_expense, orgCurrency)}</span>
                        )}
                        {policy.max_per_diem && (
                          <span>Max per diem: {formatMoney(policy.max_per_diem, orgCurrency)}</span>
                        )}
                        <span>
                          Receipt required above: {formatMoney(policy.require_receipt_above, orgCurrency)}
                        </span>
                        <span>
                          Manager approval above: {formatMoney(policy.require_manager_approval_above, orgCurrency)}
                        </span>
                      </div>
                      {policy.allowed_category_names.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {policy.allowed_category_names.map((name) => (
                            <Badge key={name} variant="outline" className="text-xs">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(policy)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(policy.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
